import React, { useCallback, useMemo, useState } from "react";
import { ReactSVG } from "react-svg";
import { useNavigate } from "react-router-dom";
import { useToast } from "@npm-telluria-tecnologia/telluria-ui";

// atoms
import {
  CircularProgressWithPercent,
  PendencyDialog,
  PrintInvoiceDialog
} from "@atoms/index";
import type { IPendency, IInvoiceFiscalDocumentArchives } from "@atoms/index";

// molecules
import { ConcretingApprovalDialog } from "@molecules/index";

// hooks
import { useDownloadProvider } from "@hooks/useDownloadArchive";

// icons
import { Alert } from "@assets/icons";

// libraries
import { ArrowForwardIos } from "@libraries/icons";
import { atom } from "@libraries/jotai";

// graphql
import {
  ArchiveType,
  UserType,
  ConcretingRequestType,
  EConcretingRequestStatus
} from "@graphql/helpers/types.graphql";

import * as Styled from "./styles";

interface BaseCardActionProps {
  type:
    | "pendency"
    | "progress"
    | "certificate"
    | "invoice"
    | "permissionUser"
    | "concretingRequest"
    | "concretingApproval";
  progressValue?: number;
  color?: string;
  hasPendency?: boolean;
  pendencies?: IPendency;
  archive?: ArchiveType;
  invoiceFiscalDocumentArchives?: IInvoiceFiscalDocumentArchives;
  user?: UserType;
  concretingRequest?: ConcretingRequestType;
  setApprovalModalIsOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  inactive?: boolean;
}

const BaseCardAction: React.FC<BaseCardActionProps> = ({
  type,
  progressValue,
  color,
  hasPendency,
  pendencies,
  archive,
  invoiceFiscalDocumentArchives,
  user,
  concretingRequest,
  setApprovalModalIsOpen,
  inactive = false
}) => {
  const navigate = useNavigate();
  const { downloadArchive } = useDownloadProvider();

  // Pendency Dialog State
  const [pendencyDialogOpen, setPendencyDialogOpen] = useState(false);
  const handlePendencyDialog = useCallback(() => {
    setPendencyDialogOpen(!pendencyDialogOpen);
  }, [pendencyDialogOpen]);

  // Print Invoice Dialog State
  const [printInvoiceDialogOpen, setPrintInvoiceDialogOpen] = useState(false);
  const handlePrintInvoiceDialog = useCallback(() => {
    setPrintInvoiceDialogOpen(!printInvoiceDialogOpen);
  }, [printInvoiceDialogOpen]);

  // Concreting Approval Dialog State
  const [concretingApprovalDialogOpen, setConcretingApprovalDialogOpen] = useState(false);
  const handleConcretingApprovalDialog = useCallback(() => {
    setConcretingApprovalDialogOpen(!concretingApprovalDialogOpen);

    setApprovalModalIsOpen
      && setApprovalModalIsOpen(!concretingApprovalDialogOpen);
  }, [concretingApprovalDialogOpen, setApprovalModalIsOpen]);

  const handlePrintArchive = useCallback(async () => {
    if (!archive) return;

    await downloadArchive(archive);
  }, [archive, downloadArchive]);

  const handlePermissionUser = useCallback(() => {
    navigate("/permission-user", { state: { userSelected: user } });
  }, [navigate, user]);

  const handleConcretingRequest = useCallback(() => {
    const onlyView = concretingRequest?.status === EConcretingRequestStatus.APPROVED;

    navigate("/concreting-request", {
      state: { concretingRequest, mode: "EDIT", onlyView }
    });
  }, [concretingRequest, navigate]);

  const handleClick = useCallback(() => {
    if (inactive) return;

    if (type === "pendency") handlePendencyDialog();

    if (type === "invoice") handlePrintInvoiceDialog();

    if (type === "certificate") handlePrintArchive();

    if (type === "permissionUser") handlePermissionUser();

    if (type === "concretingRequest") handleConcretingRequest();

    if (type === "concretingApproval") handleConcretingApprovalDialog();
  }, [
    type,
    inactive,
    handlePendencyDialog,
    handlePrintInvoiceDialog,
    handlePrintArchive,
    handlePermissionUser,
    handleConcretingApprovalDialog,
    handleConcretingRequest
  ]);

  const icon = useMemo(() => {
    if (type === "certificate" && archive) return <Styled.CustomPrint style={{ color: "#64DD17" }} />;

    if (type === "invoice") return <Styled.CustomPrint style={{ color: "#64DD17" }} />;

    if (type === "progress") return <CircularProgressWithPercent value={progressValue || 0} />;

    if (type === "pendency") {
      return (
        <Styled.IconContainer color={color || ""}>
          <ReactSVG src={Alert} />
        </Styled.IconContainer>
      );
    }

    if (type === "permissionUser") {
      return (
        <ArrowForwardIos
          fontSize="small"
          style={{ color: "#BEBEBE", cursor: `${inactive ? "auto" : "pointer"}` }}
        />
      );
    }

    if (type === "concretingRequest") return <Styled.CustomEdit />;

    if (type === "concretingApproval") return <Styled.CustomAccessTime />;

    return null;
  }, [type, progressValue, color, archive]);

  return (
    <>
      <Styled.Container onClick={handleClick}>{icon}</Styled.Container>
      {type === "concretingApproval" && concretingRequest && (
        <ConcretingApprovalDialog
          open={concretingApprovalDialogOpen}
          onClose={handleConcretingApprovalDialog}
          selectedStepTabAtom={atom<number>(0)}
          concretingRequest={concretingRequest}
        />
      )}
      {hasPendency && pendencies && (
        <PendencyDialog
          open={pendencyDialogOpen}
          onClose={handlePendencyDialog}
          title="Pendências"
          pendencies={pendencies}
        />
      )}
      {type === "invoice" && invoiceFiscalDocumentArchives && (
        <PrintInvoiceDialog
          open={printInvoiceDialogOpen}
          onClose={handlePrintInvoiceDialog}
          title="Imprimir"
          invoiceFiscalDocumentArchives={invoiceFiscalDocumentArchives}
        />
      )}
    </>
  );
};

BaseCardAction.defaultProps = {
  progressValue: 0,
  color: "",
  hasPendency: false,
  pendencies: undefined,
  archive: undefined,
  invoiceFiscalDocumentArchives: undefined,
  user: undefined,
  concretingRequest: undefined,
  setApprovalModalIsOpen: undefined,
  inactive: false
};

export default BaseCardAction;
